/**
 * BatchReportPage — Production Report module entry tại
 * `/factory/<code>/report/batch-report`.
 *
 * Spec: `frontend/plan/Excel_Production_Report_Web_UI_Conversion_Spec.md`.
 *
 * Module wrap 6 tab nội bộ tương ứng 6 sheet Excel:
 *   1. Dashboard            — executive KPI + 6 chart blocks (§8).
 *   2. Batch Report         — INPUT/OUTPUT grid (§3).
 *   3. Production Analysis  — budget/actual + loss + extracted value (§4).
 *   4. Thông tin qui chuẩn  — standardization grid (§5).
 *   5. Comparison           — current vs previous PO (§6).
 *   6. BOM-BTA1             — BOM template/config + costs (§7).
 *
 * Shared state:
 * - `poNo`             — PO selector (sync across tabs, §1.3 batch context).
 * - `mode`             — `qui chuan` / `chua qui chuan` toggle (§4.3 D3).
 * - `showAdvanced`     — Batch Report column expansion only.
 * - `activeTab`        — landing default = Dashboard (§1.2).
 *
 * Mock data hiện hardcode cho PO `PRO-TLBL-25097`. Khi BE expose
 * `/api/production-report/{po_no}/...` thì RTK Query thay block mock.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'

import {
  SAMPLE_INPUT_ROWS,
  SAMPLE_INPUT_VALUE,
  SAMPLE_OUTPUT_ROWS,
  SAMPLE_PO_NO,
} from './batchReportMock'
import { SAMPLE_PO_TYPE } from './productionReportMock'
import BatchReportTab from './tabs/BatchReportTab'
import BomTab from './tabs/BomTab'
import ComparisonTab from './tabs/ComparisonTab'
import DashboardTab from './tabs/DashboardTab'
import ProductionAnalysisTab from './tabs/ProductionAnalysisTab'
import StandardizationTab from './tabs/StandardizationTab'
import { COLORS } from './tabs/styleTokens'

type Mode = 'chua qui chuan' | 'qui chuan'

type TabKey =
  | 'dashboard'
  | 'batch-report'
  | 'production-analysis'
  | 'standardization'
  | 'comparison'
  | 'bom'

interface TabConfig {
  key: TabKey
  label: string
}

const TABS: TabConfig[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'batch-report', label: 'Batch Report' },
  { key: 'production-analysis', label: 'Production Analysis' },
  { key: 'standardization', label: 'Thông tin qui chuẩn' },
  { key: 'comparison', label: 'Comparison' },
  { key: 'bom', label: 'BOM-BTA1' },
]

export interface BatchReportPageProps {
  factoryCode: string
  onBackToHub?: () => void
}

export default function BatchReportPage({ factoryCode, onBackToHub }: BatchReportPageProps) {
  const [poNo, setPoNo] = useState(SAMPLE_PO_NO)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [mode, setMode] = useState<Mode>('chua qui chuan')
  const [activeTab, setActiveTab] = useState<TabKey>('dashboard')

  // FIXME(api): swap mock với endpoint thực khi BE sẵn sàng. PoNo input không
  // ảnh hưởng data hiện tại (chỉ display vì mock chỉ có 1 PO `PRO-TLBL-25097`).
  const inputRows = SAMPLE_INPUT_ROWS
  const outputRows = SAMPLE_OUTPUT_ROWS
  const inputValue = SAMPLE_INPUT_VALUE

  // §3.3 K1/K2 — đếm rows có StackID.
  const inputRowCount = useMemo(
    () => inputRows.filter((r) => r.stackId != null && r.stackId !== '').length,
    [inputRows],
  )
  const outputRowCount = useMemo(
    () => outputRows.filter((r) => r.stackId != null && r.stackId !== '').length,
    [outputRows],
  )

  const showAdvancedToggle = activeTab === 'batch-report'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/*
       * Title row — Back · Title · factory chip · subtitle stats inline ·
       * spacer · interactive controls (PO + mode + advanced).
       *
       * Stats hiển thị dạng subtitle compact ngay sau title (dot-separated
       * không có label) để KHÔNG wrap thành row riêng khi viewport hẹp —
       * vẫn là context info nhưng giờ chỉ tốn ~180px thay vì ~340px.
       */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        useFlexGap
        flexWrap="wrap"
        sx={{
          py: 0.5,
          px: 0.25,
          rowGap: 0.5,
        }}
      >
        {onBackToHub ? (
          <Button
            size="small"
            onClick={onBackToHub}
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            variant="text"
            sx={{
              textTransform: 'none',
              fontSize: '0.8125rem',
              color: COLORS.textMuted,
              minWidth: 0,
              px: 0.75,
              py: 0.25,
            }}
          >
            Reports
          </Button>
        ) : null}
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: COLORS.text, letterSpacing: 0.2 }}>
          Production Report
        </Typography>
        <Chip
          size="small"
          label={factoryCode.toUpperCase()}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: COLORS.primarySoft,
            color: COLORS.primary,
            border: 'none',
          }}
        />

        {/* Stats inline (subtitle metadata). Dot-separated, no labels. */}
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: COLORS.textMuted,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            '& > span.sep': { color: COLORS.textSubtle },
            '& > b': { color: COLORS.text, fontWeight: 700 },
          }}
        >
          <b>{SAMPLE_PO_TYPE}</b>
          <span className="sep">·</span>
          <b>${inputValue.toLocaleString('en-US')}</b>
          <span className="sep">·</span>
          <b>{inputRowCount}</b> in
          <span className="sep">/</span>
          <b>{outputRowCount}</b> out
        </Typography>

        {/* Spacer push controls về phải. */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        <TextField
          size="small"
          value={poNo}
          onChange={(e) => setPoNo(e.target.value)}
          placeholder="PO No."
          sx={{
            width: 170,
            bgcolor: COLORS.bgPage,
            borderRadius: 1,
            '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.4, px: 1 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
          }}
        />

        {/* Mode toggle — mirror `'Production Analysis'!D3`. */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ModeChip
            label="Non-std"
            tooltip="Non-standardized"
            active={mode === 'chua qui chuan'}
            onClick={() => setMode('chua qui chuan')}
          />
          <ModeChip
            label="Std"
            tooltip="Standardized"
            active={mode === 'qui chuan'}
            onClick={() => setMode('qui chuan')}
          />
        </Stack>

        {showAdvancedToggle ? (
          <FormControlLabel
            control={
              <Switch size="small" checked={showAdvanced} onChange={(_, v) => setShowAdvanced(v)} />
            }
            label={<Typography sx={{ fontSize: '0.75rem', color: COLORS.textMuted }}>Advanced</Typography>}
            sx={{ ml: 0, mr: 0 }}
          />
        ) : null}
      </Stack>

      {/*
       * Sub-nav row — Level-2 nav (pill / segmented control).
       *
       * Differentiation từ level-1 (top factory tabs, underline style):
       *   - Pill rounded background thay vì underline.
       *   - Active pill: solid primary bg + white text.
       *   - Wrapper bg `bgSubtle` + border → signal "section nội bộ".
       *
       * Pills full-width, scroll horizontal khi overflow (scrollbar mỏng để
       * user biết còn nội dung bên phải).
       */}
      <Box
        sx={{
          mt: 0.5,
          bgcolor: COLORS.bgSubtle,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1.5,
          px: 0.5,
          py: 0.5,
          display: 'flex',
          gap: 0.5,
          overflowX: 'auto',
          // Thin scrollbar — user biết tab có thể overflow horizontal.
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { background: COLORS.borderStrong, borderRadius: 999 },
        }}
      >
        {TABS.map((t) => {
          const active = t.key === activeTab
          return (
            <Button
              key={t.key}
              size="small"
              disableElevation
              onClick={() => setActiveTab(t.key)}
              sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                minHeight: 28,
                minWidth: 0,
                fontSize: '0.78rem',
                fontWeight: active ? 700 : 600,
                letterSpacing: 0.1,
                bgcolor: active ? COLORS.primary : 'transparent',
                color: active ? '#fff' : COLORS.textMuted,
                transition: 'background-color 120ms ease, color 120ms ease',
                '&:hover': {
                  bgcolor: active ? COLORS.primary : COLORS.bgPage,
                  color: active ? '#fff' : COLORS.text,
                },
              }}
            >
              {t.label}
            </Button>
          )
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 1.5 }}>
        {activeTab === 'dashboard' ? (
          <DashboardTab poNo={poNo} poType={SAMPLE_PO_TYPE} />
        ) : activeTab === 'batch-report' ? (
          <BatchReportTab inputRows={inputRows} outputRows={outputRows} showAdvanced={showAdvanced} />
        ) : activeTab === 'production-analysis' ? (
          <ProductionAnalysisTab />
        ) : activeTab === 'standardization' ? (
          <StandardizationTab />
        ) : activeTab === 'comparison' ? (
          <ComparisonTab poNo={poNo} />
        ) : (
          <BomTab />
        )}
      </Box>
    </Box>
  )
}

/* ---------- helpers ------------------------------------------------------ */

function ModeChip({ label, tooltip, active, onClick }: { label: string; tooltip: string; active: boolean; onClick: () => void }) {
  return (
    <Chip
      size="small"
      clickable
      label={label}
      title={tooltip}
      onClick={onClick}
      sx={{
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 600,
        px: 0.25,
        bgcolor: active ? COLORS.primary : 'transparent',
        color: active ? '#fff' : COLORS.textMuted,
        border: active ? 'none' : `1px solid ${COLORS.border}`,
        '&:hover': { bgcolor: active ? COLORS.primary : COLORS.bgHover },
      }}
    />
  )
}
